import { PropsWithChildren, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../AppContext/AppContext';
import { AppRoute } from '../../constants';
import { useOrderFlowNavigation } from '../../hooks/useOrderFlowNavigation';
import { useTranslation } from '../../i18n/i18n';
import { useOfferContext } from '../../OfferContext/OfferContext';
import { reportException } from '../../tools/sentry';
import { OrderFlow } from '../../types';
import { buildPathWithPersistedSiteId } from '../../utils/utils';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

const { energyDynamic, energyFix, batteryOptimization } = OrderFlow;

const flowsRequiringOffer = new Set([energyDynamic, energyFix, batteryOptimization]);

/**
 * DataSafeguard component ensures that the user has valid data
 * per flow before the children components are rendered.
 */
export const DataSafeguard = ({ children }: PropsWithChildren) => {
  const { orderFlow, formValues, user } = useAppContext();
  const { offer } = useOfferContext();
  const { navigatePrevious } = useOrderFlowNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    if (!orderFlow) {
      reportException(new Error('DataSafeguard: missing orderFlow, redirecting to root'), {
        tag: 'data-safeguard',
        context: { reason: 'missing-order-flow' },
      });
      navigate(AppRoute.ROOT, { replace: true });
      return;
    }
    if (!offer) {
      if ([energyDynamic, energyFix].includes(orderFlow)) {
        reportException(new Error('DataSafeguard: offer expired or missing for tariff flow'), {
          tag: 'data-safeguard',
          context: { reason: 'offer-missing', orderFlow },
        });
        navigate(buildPathWithPersistedSiteId(AppRoute.ELIGIBILITY_CHECK, location.search), {
          state: { offerExpired: true },
          replace: true,
        });
        return;
      }

      if (orderFlow === batteryOptimization) {
        reportException(new Error('DataSafeguard: offer missing for batteryOptimization flow'), {
          tag: 'data-safeguard',
          context: { reason: 'offer-missing', orderFlow },
        });
        navigate(AppRoute.ROOT, { replace: true });
      }
      return;
    }
    if (
      orderFlow === batteryOptimization &&
      !formValues.confirmedDynamicTariff &&
      navigatePrevious
    ) {
      reportException(new Error('DataSafeguard: confirmedDynamicTariff not set, navigating back'), {
        tag: 'data-safeguard',
        context: { reason: 'unconfirmed-dynamic-tariff', orderFlow },
      });
      navigatePrevious();
    }
  }, [orderFlow, offer, navigate, user, location.search]);

  if (!orderFlow) return <LoadingSpinner text={t('offer.loading')} />;
  if (!offer && flowsRequiringOffer.has(orderFlow))
    return <LoadingSpinner text={t('offer.loading')} />;

  return children;
};
